import { useLazyQuery, useMutation } from '@apollo/client';
import { Checkbox } from '@televet/kibble-ui/build/components/Checkbox';
import { Tooltip } from '@televet/kibble-ui/build/components/Tooltip';
import { Spinner } from '@televet/kibble-ui/build/components/Spinner';
import {
  AmexCardIcon,
  DinersCardIcon,
  DiscoverCardIcon,
  GenericCardIcon,
  JcbCardIcon,
  MasterCardCardIcon,
  TrashCanIcon,
  UnionPayCardIcon,
  VisaCardIcon,
} from 'assets/icons';
import React, { useCallback, useEffect, useState } from 'react';
import { GET_PAYMENT_METHODS, UPDATE_ONE_PAYMENT_METHOD } from 'shared/mutations';
import { CardBrand, CardFunding, StripePaymentMethod } from 'shared/types/graphql';
import styled from 'styled-components/macro';
import FundingTypeTag from './FundingTypeTag';
import { cardFundingTypeConverter } from '../utils/cardFundingTypeConverter';

const RenderCardBrandImage = (cardBrand: CardBrand): JSX.Element => {
  let Icon = GenericCardIcon;
  switch (cardBrand) {
    case CardBrand.Visa:
      Icon = VisaCardIcon;
      break;
    case CardBrand.Mastercard:
      Icon = MasterCardCardIcon;
      break;
    case CardBrand.Amex:
      Icon = AmexCardIcon;
      break;
    case CardBrand.Discover:
      Icon = DiscoverCardIcon;
      break;
    case CardBrand.Diners:
      Icon = DinersCardIcon;
      break;
    case CardBrand.Jcb:
      Icon = JcbCardIcon;
      break;
    case CardBrand.UnionPay:
      Icon = UnionPayCardIcon;
      break;
  }
  return <Icon style={{ width: 30, height: 20, marginLeft: 10, borderRadius: 3 }} />;
};
interface ISavedPaymentMethodsProps {
  clinicPetParentId: string;
  selectedSavedPaymentMethodStripeId: string | null;
  setSelectedSavedPaymentMethodStripeId: React.Dispatch<React.SetStateAction<string | null>>;
  setFundingType?: React.Dispatch<React.SetStateAction<CardFunding | undefined>>;
  fundingType?: CardFunding | undefined;
}

const SavedPaymentMethods = ({
  clinicPetParentId,
  selectedSavedPaymentMethodStripeId,
  setSelectedSavedPaymentMethodStripeId,
  setFundingType,
  fundingType,
}: ISavedPaymentMethodsProps): JSX.Element => {
  const [paymentMethodSelectedForDeletion, setPaymentMethodSelectedForDeletion] = useState<StripePaymentMethod | null>(
    null,
  );
  const [useDefault, setUseDefault] = useState(true);
  const [updateOneStripePaymentMethod, { loading: isPaymentMethodUpdating }] = useMutation(UPDATE_ONE_PAYMENT_METHOD, {
    onCompleted: () => {
      // here we're deciding to show card details no matter which card is deleted
      setUseDefault(false);
      setSelectedSavedPaymentMethodStripeId(null);
      setFundingType && setFundingType(undefined);
    },
  });

  const removeSavedPaymentMethod = useCallback(
    async (id: string) => {
      await updateOneStripePaymentMethod({
        variables: { where: { id }, data: { isReusable: false, isDefault: false, isDeleted: true } },
      });
      setPaymentMethodSelectedForDeletion(null);
      setFundingType && setFundingType(undefined);
    },
    [updateOneStripePaymentMethod, setFundingType],
  );

  const [getSavedPaymentMethods, { data: savedPaymentsData }] = useLazyQuery<{
    findManyStripePaymentMethod: StripePaymentMethod[];
  }>(GET_PAYMENT_METHODS, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      // covers edge case where default is deleted and no transaction occurs afterwards
      if (data.findManyStripePaymentMethod.length === 1 && !data.findManyStripePaymentMethod[0].isExpired) {
        setUseDefault(false);
        setSelectedSavedPaymentMethodStripeId(data.findManyStripePaymentMethod[0].stripeId);
        setFundingType &&
          setFundingType(cardFundingTypeConverter(data.findManyStripePaymentMethod[0]?.funding ?? undefined));
        return;
      }
      // in the event that the pet parent has a default payment method, set the selected stripeId
      data.findManyStripePaymentMethod.find((paymentMethod) => {
        if (paymentMethod.isDefault && !paymentMethod.isExpired) {
          setSelectedSavedPaymentMethodStripeId(paymentMethod.stripeId);
          setFundingType && setFundingType(cardFundingTypeConverter(paymentMethod?.funding ?? undefined));
          return;
        }
      });
    },
  });

  const fetchSavedPayments = useCallback(() => {
    getSavedPaymentMethods({
      variables: {
        where: {
          clinicPetParent: { id: { equals: clinicPetParentId } },
          isReusable: { equals: true },
        },
      },
    });
  }, [clinicPetParentId, getSavedPaymentMethods]);

  useEffect(() => {
    if (clinicPetParentId) {
      fetchSavedPayments();
    }
  }, [clinicPetParentId, fetchSavedPayments]);

  return (
    <div data-testid="saved-payment-methods-container">
      {clinicPetParentId && (
        <div>
          {savedPaymentsData?.findManyStripePaymentMethod.map((paymentMethod) => {
            if (paymentMethod.stripeCustomerId)
              return (
                <PaymentMethod key={paymentMethod.id}>
                  &nbsp;&nbsp;&nbsp;&nbsp;
                  {paymentMethod.isExpired ? (
                    <Tooltip
                      label={`This payment method expired on ${
                        paymentMethod.expirationMonth > 9
                          ? paymentMethod.expirationMonth
                          : '0' + paymentMethod.expirationMonth
                      }/${paymentMethod.expirationYear}.`}
                    >
                      <Expired>Expired</Expired>
                    </Tooltip>
                  ) : (
                    <Checkbox
                      isFieldInline
                      isChecked={
                        useDefault
                          ? paymentMethod.isDefault === true
                          : paymentMethod.stripeId === selectedSavedPaymentMethodStripeId
                      }
                      onChange={(): void => {
                        setUseDefault(false);
                        if (paymentMethod.stripeId === selectedSavedPaymentMethodStripeId) {
                          setSelectedSavedPaymentMethodStripeId(null);
                          setFundingType && setFundingType(undefined);
                        } else {
                          setSelectedSavedPaymentMethodStripeId(paymentMethod.stripeId);
                          setFundingType &&
                            setFundingType(cardFundingTypeConverter(paymentMethod?.funding ?? undefined));
                        }
                      }}
                    />
                  )}
                  &nbsp;&nbsp;
                  <CardDetails isExpired={!!paymentMethod.isExpired}>
                    {paymentMethod.cardBrand} •••• {paymentMethod.last4} {RenderCardBrandImage(paymentMethod.cardBrand)}
                  </CardDetails>
                  <StyledTrashCanIcon onClick={(): void => setPaymentMethodSelectedForDeletion(paymentMethod)} />
                </PaymentMethod>
              );
          })}
          {!!savedPaymentsData?.findManyStripePaymentMethod && (
            <FundingTypeTag
              fundingType={fundingType ?? undefined}
              isCardSelected={!!selectedSavedPaymentMethodStripeId}
            />
          )}
          {paymentMethodSelectedForDeletion && (
            <RemovePaymentMethodConfirmation>
              Are you sure you want to remove {paymentMethodSelectedForDeletion?.cardBrand} card ••••{' '}
              {paymentMethodSelectedForDeletion?.last4}?
              {!isPaymentMethodUpdating ? (
                <RemovePaymentActionContainer>
                  <RemovePaymentMethodOption onClick={(): void => setPaymentMethodSelectedForDeletion(null)}>
                    Cancel
                  </RemovePaymentMethodOption>
                  <RemovePaymentMethodOption
                    onClick={(): Promise<void> => removeSavedPaymentMethod(paymentMethodSelectedForDeletion.id)}
                  >
                    Yes
                  </RemovePaymentMethodOption>
                </RemovePaymentActionContainer>
              ) : (
                <Loading>
                  <Spinner />
                </Loading>
              )}
            </RemovePaymentMethodConfirmation>
          )}
        </div>
      )}
    </div>
  );
};

export default SavedPaymentMethods;

const PaymentMethod = styled.div`
  display: flex;
  align-items: center;
`;

const Expired = styled.div`
  color: #fff;
  background-color: #e47792;
  padding: 2px 5px;
  border-radius: 4px;
  font-size: 14px;
`;

const RemovePaymentMethodOption = styled.div`
  padding: 5px 10px;
  border-radius: 8px;
  border: 1px solid var(--chakra-colors-text-default);
  min-width: 72px;
  text-align: center;
  margin-top: 15px;
  cursor: pointer;
`;

const StyledTrashCanIcon = styled(TrashCanIcon)`
  cursor: pointer;
  margin-left: 5px;
  width: 15px;
  height: 15px;
`;

const RemovePaymentActionContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-evenly;
`;

const RemovePaymentMethodConfirmation = styled.div`
  max-width: 400px;
  margin: 20px auto;
  padding: 10px;
  border-radius: 8px;
  background-color: #f5f5f5;

  text-align: center;
`;

const Loading = styled.div`
  display: flex;
  justify-content: center;
`;

const CardDetails = styled.div<{ isExpired: boolean }>`
  display: flex;
  width: 244px;
  margin-left: 10px;
  margin-right: ${({ isExpired }): string => (isExpired ? '-43px' : '0px')};
`;
